/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Library, Search, Sparkles, Trash2, Type, Download, X, Save, Image as ImageIcon, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ModuleAccent from "@/components/ModuleAccent";

const PRIMARY = "#7C3AED";
const ACCENT = "#FF1A5C";

const FORMATS = [
    { v: "", l: "Tutti" },
    { v: "square", l: "1:1" },
    { v: "portrait", l: "4:5" },
    { v: "story", l: "9:16" },
    { v: "landscape", l: "16:9" },
];

/**
 * Asset Library per brand — ogni immagine generata viene salvata qui.
 * Funzioni:
 *  • Browse griglia con filtri (formato, ricerca, tipo)
 *  • Re-genera variante a partire da un asset (passa brief al Copy Studio)
 *  • Download PNG
 *  • Text Overlay Editor: aggiunge testo controllato pixel-perfect sopra l'immagine
 *  • Elimina asset
 */
export default function TotalKlickADSLibrary() {
    const { brandId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [brand, setBrand] = useState(null);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [fmt, setFmt] = useState("");
    const [editingAsset, setEditingAsset] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: bd }, { data: list }] = await Promise.all([
                api.get(`/ads/brands/${brandId}`),
                api.get(`/ads/brands/${brandId}/assets`, { params: { q: q || undefined, fmt: fmt || undefined, limit: 80 } }),
            ]);
            setBrand(bd.brand);
            setAssets(list);
            // Auto-open editor if ?edit=ID is in URL
            const editId = searchParams.get("edit");
            if (editId) {
                try {
                    const { data: asset } = await api.get(`/ads/brands/${brandId}/assets/${editId}`);
                    setEditingAsset(asset);
                    searchParams.delete("edit");
                    setSearchParams(searchParams, { replace: true });
                } catch (e) { /* asset removed or no auth */ }
            }
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [brandId, fmt]);

    const onSearch = (e) => { e.preventDefault(); load(); };

    const remove = async (id) => {
        if (!window.confirm("Eliminare questo asset definitivamente?")) return;
        try {
            await api.delete(`/ads/brands/${brandId}/assets/${id}`);
            setAssets((arr) => arr.filter((a) => a.id !== id));
        } catch (e) { setErr(formatApiError(e)); }
    };

    const openEditor = async (asset) => {
        try {
            const { data } = await api.get(`/ads/brands/${brandId}/assets/${asset.id}`);
            setEditingAsset(data);
        } catch (e) { setErr(formatApiError(e)); }
    };

    const onSavedComposed = (newAssetId) => {
        setEditingAsset(null);
        load();
    };

    return (
        <ModuleAccent module="ads" as="div" style={{ fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif', background: "#0A1024", color: "#fff", minHeight: "100vh" }}>
            <header className="px-6 md:px-10 py-3.5 flex items-center justify-between sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(10,16,36,0.92)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <Link to={`/totalklickads/brand/${brandId}`} className="flex items-center gap-2 text-[13px] font-bold hover:opacity-70"><ArrowLeft size={14} /> Brand DNA</Link>
                <div className="flex items-center gap-3">
                    <Link to={`/totalklickads/brand/${brandId}/copy`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-extrabold text-[12.5px]" style={{ background: "rgba(124,58,237,0.18)", color: "#C4B5FD", border: "1px solid rgba(124,58,237,0.35)" }} data-testid="lib-open-copy-studio">
                        <Sparkles size={13} /> Copy Studio AI
                    </Link>
                </div>
            </header>

            <section className="px-6 md:px-10 py-8">
                <div className="max-w-6xl mx-auto">
                    <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
                        <div>
                            <div className="mono text-[10px] uppercase tracking-[0.2em] font-extrabold opacity-60 flex items-center gap-2"><Library size={11} /> ASSET LIBRARY</div>
                            <h1 className="text-[32px] md:text-[42px] font-extrabold tracking-tighter mt-1">{brand?.name || "—"}</h1>
                            <p className="text-[13px] opacity-70 mt-1">Tutto il materiale generato dall&apos;AI per questo brand. Riutilizzabile, scaricabile, modificabile.</p>
                        </div>
                        <div className="mono text-[10px] uppercase tracking-wider font-extrabold px-3 py-1.5 rounded-full" style={{ background: "rgba(124,58,237,0.18)", color: "#C4B5FD" }} data-testid="lib-count">
                            {assets.length} asset
                        </div>
                    </div>

                    {/* Filtri */}
                    <form onSubmit={onSearch} className="flex items-center gap-2 mb-5 flex-wrap" data-testid="lib-filters">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="cerca per brief, titolo, tag…" className="w-full pl-9 pr-3 py-2.5 rounded-full text-[13px] outline-none" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff" }} data-testid="lib-search" />
                        </div>
                        {FORMATS.map((f) => (
                            <button key={f.v} type="button" onClick={() => setFmt(f.v)} className="px-3 py-2 rounded-full text-[12px] font-bold transition" style={{
                                background: fmt === f.v ? `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` : "rgba(255,255,255,0.06)",
                                color: fmt === f.v ? "#fff" : "#C4B5FD",
                                border: fmt === f.v ? "none" : "1px solid rgba(255,255,255,0.12)",
                            }} data-testid={`lib-fmt-${f.v || 'all'}`}>{f.l}</button>
                        ))}
                        <button type="submit" className="px-3 py-2 rounded-full text-[12px] font-bold" style={{ background: "rgba(124,58,237,0.18)", color: "#C4B5FD", border: "1px solid rgba(124,58,237,0.35)" }} data-testid="lib-search-submit">
                            <RefreshCw size={12} className="inline mr-1" /> Filtra
                        </button>
                    </form>

                    {err && <div className="mb-4 p-3 rounded-lg text-[13px]" style={{ background: "rgba(239,68,68,0.12)", color: "#FCA5A5" }} data-testid="lib-error">{err}</div>}

                    {loading ? (
                        <div className="text-center opacity-60 py-12 text-[14px]">Caricamento libreria…</div>
                    ) : assets.length === 0 ? (
                        <div className="text-center py-16 rounded-2xl" style={{ background: "rgba(124,58,237,0.05)", border: "1px dashed rgba(124,58,237,0.25)" }} data-testid="lib-empty">
                            <ImageIcon size={36} className="mx-auto opacity-40 mb-3" />
                            <div className="text-[15px] font-bold mb-1">Nessun asset ancora</div>
                            <p className="text-[12.5px] opacity-65 mb-4">Genera la prima immagine dal Copy Studio AI</p>
                            <Link to={`/totalklickads/brand/${brandId}/copy`} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white text-[13px] font-extrabold" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` }} data-testid="lib-create-first">
                                <Sparkles size={13} /> Crea ora
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {assets.map((a) => (
                                <AssetCard key={a.id} asset={a} brandId={brandId} onDelete={() => remove(a.id)} onEdit={() => openEditor(a)} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {editingAsset && (
                <TextOverlayEditor
                    asset={editingAsset}
                    brandId={brandId}
                    onClose={() => setEditingAsset(null)}
                    onSaved={onSavedComposed}
                />
            )}
        </ModuleAccent>
    );
}

function AssetCard({ asset, brandId, onDelete, onEdit }) {
    const [src, setSrc] = useState(null);
    const fetchOnView = async () => {
        if (src) return;
        try {
            const { data } = await api.get(`/ads/brands/${brandId}/assets/${asset.id}`);
            setSrc(data.data_uri);
        } catch (e) { /* ignore */ }
    };
    useEffect(() => { fetchOnView(); }, []);

    const fmtBadge = { square: "1:1", portrait: "4:5", story: "9:16", landscape: "16:9" }[asset.format] || asset.format;
    const kindBadge = asset.kind === "composed" ? "+ TESTO" : asset.kind === "upload" ? "UPLOAD" : "AI";
    return (
        <div className="rounded-2xl overflow-hidden group relative" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`lib-asset-${asset.id}`}>
            <div className="aspect-square relative bg-black">
                {src ? (
                    <img src={src} alt={asset.brief || asset.title || "asset"} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                    <div className="absolute inset-0 grid place-items-center opacity-40 text-[11px]">Caricamento…</div>
                )}
                <div className="absolute top-2 left-2 flex gap-1.5">
                    <span className="mono text-[9px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,0.55)", color: "#fff", backdropFilter: "blur(8px)" }}>{fmtBadge}</span>
                    <span className="mono text-[9px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full" style={{ background: asset.kind === "composed" ? "rgba(196,181,253,0.92)" : "rgba(124,58,237,0.82)", color: "#fff" }}>{kindBadge}</span>
                </div>
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1.5 p-2 opacity-0 group-hover:opacity-100 transition" style={{ background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.78))" }}>
                    <IconBtn title="Aggiungi testo" onClick={onEdit} testid={`lib-asset-${asset.id}-overlay`}><Type size={13} /></IconBtn>
                    <IconBtn title="Scarica" onClick={() => src && downloadDataUri(src, `${(asset.title || asset.brief || "asset").slice(0,40)}.png`)} testid={`lib-asset-${asset.id}-download`}><Download size={13} /></IconBtn>
                    <IconBtn title="Elimina" onClick={onDelete} testid={`lib-asset-${asset.id}-delete`} danger><Trash2 size={13} /></IconBtn>
                </div>
            </div>
            <div className="p-2.5">
                <div className="text-[12px] font-bold line-clamp-1">{asset.title || asset.brief || "Senza titolo"}</div>
                <div className="text-[10.5px] opacity-55 mt-0.5 line-clamp-1">{new Date(asset.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</div>
            </div>
        </div>
    );
}

function IconBtn({ children, onClick, title, testid, danger }) {
    return (
        <button type="button" onClick={onClick} title={title} className="w-8 h-8 rounded-full grid place-items-center transition" style={{ background: danger ? "rgba(239,68,68,0.85)" : "rgba(255,255,255,0.18)", color: "#fff", backdropFilter: "blur(8px)" }} data-testid={testid}>
            {children}
        </button>
    );
}

function downloadDataUri(dataUri, filename) {
    const a = document.createElement("a");
    a.href = dataUri;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
}

/**
 * TextOverlayEditor — modal full-screen che renderizza l'immagine su <canvas>
 * con testo posizionabile, font/size/color/shadow controllabili, drag&drop posizione.
 * Salva il risultato come asset "composed" via POST /assets.
 */
function TextOverlayEditor({ asset, brandId, onClose, onSaved }) {
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const containerRef = useRef(null);
    const [renderedDims, setRenderedDims] = useState({ w: 600, h: 600 });
    const [opts, setOpts] = useState({
        text: "Il tuo testo qui",
        font: "Plus Jakarta Sans",
        size: 64,
        weight: 800,
        color: "#FFFFFF",
        align: "center",
        x: 0.5,           // normalizzata 0..1
        y: 0.85,
        shadow: true,
        bg: false,
        bgColor: "rgba(0,0,0,0.65)",
        letterSpacing: 0,
        lineHeight: 1.15,
        rotate: 0,
    });
    const [saving, setSaving] = useState(false);
    const [savedErr, setSavedErr] = useState("");
    const draggingRef = useRef(false);

    // Carico l'immagine sorgente
    useEffect(() => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            imgRef.current = img;
            const ratio = img.width / img.height;
            const maxW = 720, maxH = 720;
            let w = maxW, h = maxW / ratio;
            if (h > maxH) { h = maxH; w = maxH * ratio; }
            setRenderedDims({ w: Math.round(w), h: Math.round(h), naturalW: img.width, naturalH: img.height });
            draw({ img, dims: { w, h, naturalW: img.width, naturalH: img.height }, opts });
        };
        img.src = asset.data_uri;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-draw on opts change
    useEffect(() => {
        if (!imgRef.current) return;
        draw({ img: imgRef.current, dims: renderedDims, opts });
    }, [opts, renderedDims]);

    const draw = ({ img, dims, opts }) => {
        const canvas = canvasRef.current;
        if (!canvas || !img) return;
        // dimensioni native per qualità
        canvas.width = dims.naturalW || img.width;
        canvas.height = dims.naturalH || img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        if (!opts.text) return;

        const cx = canvas.width * opts.x;
        const cy = canvas.height * opts.y;
        // Scaling font in proporzione alla larghezza nativa per consistenza
        const sizePx = (opts.size / 720) * canvas.width;
        ctx.save();
        ctx.translate(cx, cy);
        if (opts.rotate) ctx.rotate((opts.rotate * Math.PI) / 180);
        ctx.font = `${opts.weight} ${sizePx}px "${opts.font}", system-ui, sans-serif`;
        ctx.textAlign = opts.align;
        ctx.textBaseline = "middle";
        const lines = String(opts.text).split("\n");
        const lineH = sizePx * opts.lineHeight;
        const totalH = lineH * lines.length;
        const startY = -totalH / 2 + lineH / 2;

        // Background rect optional
        if (opts.bg) {
            const maxW = Math.max(...lines.map((ln) => ctx.measureText(ln).width));
            const pad = sizePx * 0.35;
            ctx.fillStyle = opts.bgColor;
            const rectX = opts.align === "center" ? -maxW / 2 - pad : (opts.align === "right" ? -maxW - pad : -pad);
            const rectY = startY - lineH / 2 - pad / 2;
            ctx.fillRect(rectX, rectY, maxW + pad * 2, totalH + pad);
        }

        // Shadow
        if (opts.shadow) {
            ctx.shadowColor = "rgba(0,0,0,0.55)";
            ctx.shadowBlur = sizePx * 0.12;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = sizePx * 0.04;
        }
        ctx.fillStyle = opts.color;
        lines.forEach((ln, i) => {
            ctx.fillText(ln, 0, startY + i * lineH);
        });
        ctx.restore();
    };

    const moveAt = React.useCallback((clientX, clientY) => {
        if (!draggingRef.current || !containerRef.current) return;
        const r = containerRef.current.getBoundingClientRect();
        const x = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
        const y = Math.min(1, Math.max(0, (clientY - r.top) / r.height));
        setOpts((o) => ({ ...o, x, y }));
    }, []);
    const onMouseDown = (e) => { e.preventDefault(); draggingRef.current = true; };
    const onMouseMove = (e) => moveAt(e.clientX, e.clientY);
    const onTouchMove = (e) => { if (e.touches[0]) moveAt(e.touches[0].clientX, e.touches[0].clientY); };

    const save = async () => {
        setSaving(true); setSavedErr("");
        try {
            const dataUrl = canvasRef.current.toDataURL("image/png");
            const b64 = dataUrl.split(",")[1];
            const { data } = await api.post(`/ads/brands/${brandId}/assets`, {
                kind: "composed",
                parent_asset_id: asset.id,
                format: asset.format,
                mime_type: "image/png",
                data_b64: b64,
                text_overlay: opts,
                title: opts.text.slice(0, 60),
            });
            onSaved?.(data.id);
        } catch (e) {
            setSavedErr(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-3 md:p-6" style={{ background: "rgba(5,7,16,0.85)", backdropFilter: "blur(18px)" }} data-testid="overlay-editor">
            <div className="rounded-3xl max-w-6xl w-full max-h-full overflow-hidden grid lg:grid-cols-[1fr_320px]" style={{ background: "#0F1226", border: "1px solid rgba(124,58,237,0.35)", boxShadow: "0 40px 100px -20px rgba(0,0,0,0.8)" }}>
                {/* Preview */}
                <div className="relative flex items-center justify-center p-4 md:p-8" style={{ background: "radial-gradient(ellipse at center, rgba(124,58,237,0.15), transparent 70%), #0A0C1A", minHeight: 360 }}>
                    <div
                        ref={containerRef}
                        className="relative cursor-grab active:cursor-grabbing"
                        style={{ width: renderedDims.w, height: renderedDims.h, maxWidth: "100%", maxHeight: "70vh" }}
                        onMouseDown={onMouseDown}
                        onMouseMove={onMouseMove}
                        onMouseUp={() => { draggingRef.current = false; }}
                        onMouseLeave={() => { draggingRef.current = false; }}
                        onTouchStart={onMouseDown}
                        onTouchMove={onTouchMove}
                        onTouchEnd={() => { draggingRef.current = false; }}
                    >
                        <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block", borderRadius: 12, boxShadow: "0 20px 60px -12px rgba(0,0,0,0.6)" }} data-testid="overlay-canvas" />
                        {/* drag handle hint */}
                        <div className="absolute pointer-events-none" style={{ left: `${opts.x * 100}%`, top: `${opts.y * 100}%`, transform: "translate(-50%, -50%)" }}>
                            <div className="w-3 h-3 rounded-full ring-2 ring-white/80" style={{ background: PRIMARY }} />
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="p-5 overflow-y-auto" style={{ borderLeft: "1px solid rgba(255,255,255,0.06)" }}>
                    <div className="flex items-center justify-between mb-3">
                        <div className="font-extrabold text-[15px] flex items-center gap-2 text-white"><Type size={14} style={{ color: PRIMARY }} /> Text Overlay</div>
                        <button onClick={onClose} className="w-8 h-8 rounded-full grid place-items-center hover:bg-white/8 text-white/70" aria-label="Chiudi" data-testid="overlay-close"><X size={15} /></button>
                    </div>
                    <Field label="Testo">
                        <textarea rows="3" value={opts.text} onChange={(e) => setOpts({ ...opts, text: e.target.value })} className="overlay-inp" data-testid="overlay-text" />
                    </Field>
                    <Field label="Font">
                        <select value={opts.font} onChange={(e) => setOpts({ ...opts, font: e.target.value })} className="overlay-inp" data-testid="overlay-font">
                            <option>Plus Jakarta Sans</option>
                            <option>Inter</option>
                            <option>Geist</option>
                            <option>Outfit</option>
                            <option>Georgia</option>
                            <option>Times New Roman</option>
                            <option>Impact</option>
                            <option>Courier New</option>
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Dimensione">
                            <input type="range" min="20" max="180" value={opts.size} onChange={(e) => setOpts({ ...opts, size: +e.target.value })} className="w-full" data-testid="overlay-size" />
                            <div className="mono text-[10px] opacity-60 mt-0.5 text-white">{opts.size}px</div>
                        </Field>
                        <Field label="Peso">
                            <select value={opts.weight} onChange={(e) => setOpts({ ...opts, weight: +e.target.value })} className="overlay-inp" data-testid="overlay-weight">
                                <option value="300">Light</option>
                                <option value="400">Regular</option>
                                <option value="600">Semibold</option>
                                <option value="700">Bold</option>
                                <option value="800">Extra Bold</option>
                                <option value="900">Black</option>
                            </select>
                        </Field>
                    </div>
                    <Field label="Colore">
                        <div className="flex items-center gap-2">
                            <input type="color" value={opts.color} onChange={(e) => setOpts({ ...opts, color: e.target.value })} className="h-9 w-12 rounded cursor-pointer" data-testid="overlay-color" />
                            <input value={opts.color} onChange={(e) => setOpts({ ...opts, color: e.target.value })} className="overlay-inp flex-1 mono" />
                        </div>
                    </Field>
                    <Field label="Allineamento">
                        <div className="flex gap-2">
                            {["left", "center", "right"].map((al) => (
                                <button key={al} type="button" onClick={() => setOpts({ ...opts, align: al })} className="flex-1 px-3 py-1.5 rounded-full text-[11px] font-bold transition" style={{ background: opts.align === al ? `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})` : "rgba(255,255,255,0.06)", color: opts.align === al ? "#fff" : "#C4B5FD", border: opts.align === al ? "none" : "1px solid rgba(255,255,255,0.12)" }} data-testid={`overlay-align-${al}`}>{al}</button>
                            ))}
                        </div>
                    </Field>
                    <Field label="Rotazione">
                        <input type="range" min="-45" max="45" value={opts.rotate} onChange={(e) => setOpts({ ...opts, rotate: +e.target.value })} className="w-full" data-testid="overlay-rotate" />
                        <div className="mono text-[10px] opacity-60 mt-0.5 text-white">{opts.rotate}°</div>
                    </Field>
                    <div className="flex items-center gap-2 mt-2 text-white">
                        <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
                            <input type="checkbox" checked={opts.shadow} onChange={(e) => setOpts({ ...opts, shadow: e.target.checked })} style={{ accentColor: PRIMARY }} data-testid="overlay-shadow" /> ombra
                        </label>
                        <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
                            <input type="checkbox" checked={opts.bg} onChange={(e) => setOpts({ ...opts, bg: e.target.checked })} style={{ accentColor: PRIMARY }} data-testid="overlay-bg" /> sfondo
                        </label>
                    </div>
                    {opts.bg && (
                        <Field label="Colore sfondo">
                            <input value={opts.bgColor} onChange={(e) => setOpts({ ...opts, bgColor: e.target.value })} className="overlay-inp mono" data-testid="overlay-bg-color" />
                        </Field>
                    )}

                    <p className="text-[11px] opacity-55 mt-3 mb-2 text-white">💡 Trascina sull&apos;immagine per posizionare il testo.</p>

                    {savedErr && <div className="mt-2 p-2 rounded text-[12px]" style={{ background: "rgba(239,68,68,0.15)", color: "#FCA5A5" }}>{savedErr}</div>}

                    <button onClick={save} disabled={saving} className="w-full mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-full font-extrabold text-[13px] text-white disabled:opacity-50" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, boxShadow: "0 14px 40px -12px rgba(124,58,237,0.6)" }} data-testid="overlay-save">
                        <Save size={13} /> {saving ? "Salvataggio…" : "Salva nuovo asset"}
                    </button>

                    <style>{`
                        .overlay-inp { width:100%; padding:8px 10px; border-radius:8px; border:1px solid rgba(255,255,255,0.12); background:rgba(255,255,255,0.06); color:#fff; font-size:12.5px; outline:none; font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
                        .overlay-inp:focus { border-color: ${PRIMARY}; }
                    `}</style>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="mb-3">
            <label className="mono text-[10px] uppercase tracking-wider font-bold mb-1.5 block text-white" style={{ color: "#C4B5FD" }}>{label}</label>
            {children}
        </div>
    );
}
